/**
 * Reading a CSV in the console (DESIGN_EDITING.md console flows 3 and 9): the delimiter, the header, a small preview,
 * and the UTF-8 / BOM checks. Pure string work with no imports (dataView.ts says why); the component reads the file
 * and decodes the bytes.
 */

export type Delimiter = 'comma' | 'semicolon' | 'tab' | 'pipe';
export const DELIMITER_CHAR: Record<Delimiter, string> = { comma: ',', semicolon: ';', tab: '\t', pipe: '|' };
export const DELIMITER_LABEL: Record<Delimiter, string> = { comma: 'Comma', semicolon: 'Semicolon', tab: 'Tab', pipe: 'Pipe' };

const BOM = '﻿';

/** Strip a leading byte-order mark, so the first header field is clean. */
export const stripBom = (s: string): string => (s.startsWith(BOM) ? s.slice(1) : s);

/** The bytes decode as UTF-8: the component uses TextDecoder({fatal:true}); this guards an already-decoded string. */
export const looksUtf8 = (s: string): boolean => !s.includes('�');

/** The delimiter whose character is most common in the first line; comma breaks a tie. */
export const detectDelimiter = (text: string): Delimiter => {
    const firstLine = stripBom(text).split(/\r?\n/, 1)[0] ?? '';
    let best: Delimiter = 'comma';
    let bestCount = -1;
    for (const d of ['comma', 'semicolon', 'tab', 'pipe'] as Delimiter[]) {
        const n = firstLine.split(DELIMITER_CHAR[d]).length - 1;
        if (n > bestCount) { bestCount = n; best = d; }
    }
    return best;
};

/** Split one CSV line on the delimiter, honouring double-quoted fields ("a,b" is one field; "" is an escaped quote). */
export const splitCsvLine = (line: string, delim: string): string[] => {
    const out: string[] = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (inQuotes) {
            if (c === '"') {
                if (line[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
            } else field += c;
        } else if (c === '"') {
            inQuotes = true;
        } else if (c === delim) {
            out.push(field); field = '';
        } else field += c;
    }
    out.push(field);
    return out;
};

export interface CsvShape {
    header: string[];
    /** Up to `maxRows` data rows, each already split into fields. */
    rows: string[][];
}

/** The header and the first `maxRows` data rows, split on the delimiter. Blank trailing lines are dropped. */
export const readCsv = (text: string, delim: Delimiter, maxRows = 5): CsvShape => {
    const d = DELIMITER_CHAR[delim];
    const lines = stripBom(text).split(/\r?\n/);
    while (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();
    if (lines.length === 0) return { header: [], rows: [] };
    const header = splitCsvLine(lines[0], d);
    const rows = lines.slice(1, 1 + maxRows).map((l) => splitCsvLine(l, d));
    return { header, rows };
};

/** Every data value in one column of the preview (by index), the empty ones kept out. */
export const columnValues = (shape: CsvShape, col: number): string[] =>
    shape.rows.map((r) => r[col] ?? '').filter((v) => v !== '');

/** A file size for the modal's subtitle: "2.4 MB", "956 KB", "12 bytes". */
export const humanSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} bytes`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

export interface HeaderMatch {
    /** The table's own column names, in the file's order, for the columns the file has. */
    columns: string[];
    /** The file's headers (as written) that match no column: "Not in this table: a, b." disables Import. */
    notInTable: string[];
}

/**
 * Match a CSV header to a table's columns (DESIGN_EDITING.md console flow 9): each header exactly first, then ignoring
 * case. A matched header resolves to the table's own spelling, so the import names real columns; the rest are reported.
 */
export const resolveHeader = (header: string[], tableColumns: string[]): HeaderMatch => {
    const byExact = new Map(tableColumns.map((c) => [c, c]));
    const byLower = new Map(tableColumns.map((c) => [c.toLowerCase(), c]));
    const columns: string[] = [];
    const notInTable: string[] = [];
    for (const h of header) {
        const hit = byExact.get(h) ?? byLower.get(h.toLowerCase());
        if (hit === undefined) notInTable.push(h);
        else columns.push(hit);
    }
    return { columns, notInTable };
};
